import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Stethoscope, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClinicalResearchForm = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    contact_name: "",
    contact_email: "",
    contact_affiliation: "",
    research_title: "",
    research_description: "",
    support_needed: "",
    deadline: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("contact_name", formData.contact_name);
      formDataToSend.append("contact_email", formData.contact_email);
      if (formData.contact_affiliation) {
        formDataToSend.append("contact_affiliation", formData.contact_affiliation);
      }
      formDataToSend.append("research_title", formData.research_title);
      formDataToSend.append("research_description", formData.research_description);
      formDataToSend.append("support_needed", formData.support_needed);
      if (formData.deadline) {
        formDataToSend.append("deadline", formData.deadline);
      }
      if (file) {
        formDataToSend.append("file", file);
      }

      await axios.post(`${API}/submissions/methodological`, formDataToSend, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Clinical research advisory request submitted successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center mb-4">
              <Stethoscope className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-xl sm:text-2xl" >
              Clinical Research Advisory Services
            </CardTitle>
            <CardDescription className="text-base">
              Specialised support for clinical research planning and implementation
            </CardDescription>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 italic">
                <strong>Examples:</strong> Clinical trial design and methodology, participant recruitment and retention strategies, 
                data integrity, monitoring, and documentation, ethical and governance compliance, preparation of trial reports and dissemination materials
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                
                <div>
                  <Label htmlFor="contact_name">Full Name *</Label>
                  <Input
                    id="contact_name"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Enter your full name"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_email">Email Address *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    required
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="Enter your email address"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_affiliation">Affiliation (Optional)</Label>
                  <Input
                    id="contact_affiliation"
                    value={formData.contact_affiliation}
                    onChange={(e) => setFormData({ ...formData, contact_affiliation: e.target.value })}
                    placeholder="Enter your institution or organization"
                    className="mt-2"
                  />
                </div>
              </div>

              {/* Research Details Section */}
              <div className="space-y-4 border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900">Clinical Research Details</h3>

                <div>
                  <Label htmlFor="research_title">Research Title/Study Name *</Label>
                  <Input
                    id="research_title"
                    required
                    value={formData.research_title}
                    onChange={(e) => setFormData({ ...formData, research_title: e.target.value })}
                    placeholder="Enter your research or clinical trial title"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="research_description">Research Description *</Label>
                  <Textarea
                    id="research_description"
                    required
                    value={formData.research_description}
                    onChange={(e) => setFormData({ ...formData, research_description: e.target.value })}
                    placeholder="Describe your clinical research project, study design, and population"
                    rows={5}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="support_needed">Advisory Support Needed *</Label>
                  <Textarea
                    id="support_needed"
                    required
                    value={formData.support_needed}
                    onChange={(e) => setFormData({ ...formData, support_needed: e.target.value })}
                    placeholder="Describe the specific advisory support you need (e.g., trial design, recruitment strategies, ethical compliance, data monitoring)"
                    rows={5}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="deadline">Expected Timeline/Deadline (Optional)</Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    className="mt-2"
                  />
                </div>
              </div>

              {/* Disclaimer Section */}
              <div className="border-t pt-4">
                <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="disclaimer-checkbox"
                    checked={disclaimerAccepted}
                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    required
                  />
                  <label htmlFor="disclaimer-checkbox" className="text-sm text-gray-700">
                    I/we agree with the{" "}
                    <button
                      type="button"
                      onClick={() => setShowDisclaimerModal(true)}
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      disclaimer
                    </button>
                    {" "}by the EviDent Research Consultancy Ltd
                  </label>
                </div>
              </div>

              {/* File Upload Section */}
              <div className="space-y-4 border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900">Supporting Documents (Optional)</h3>
                <div>
                  <Label htmlFor="file">Upload Protocol, Study Design, or Related Documents</Label>
                  <Input
                    id="file"
                    type="file"
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      if (selectedFile) {
                        if (selectedFile.size > 10 * 1024 * 1024) {
                          toast.error(`File too large! Maximum size is 10 MB. Your file is ${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`);
                          e.target.value = '';
                          setFile(null);
                        } else {
                          setFile(selectedFile);
                        }
                      }
                    }}
                    className="mt-2"
                    accept=".pdf,.doc,.docx,.txt"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Accepted formats: PDF, DOC, DOCX, TXT (Max 10MB)
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <Button
                  type="submit"
                  disabled={submitting || !disclaimerAccepted}
                  className="w-full bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting..." : "Submit Clinical Research Request"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Disclaimer Modal */}
      <Dialog open={showDisclaimerModal} onOpenChange={setShowDisclaimerModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Research Advice Disclaimer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
              <p className="text-sm sm:text-base leading-relaxed text-gray-800">
                The information, guidance, and support provided by <strong>EVIDENT RESEARCH CONSULTANCY LTD</strong>, 
                including evidence synthesis, research methods consultancy, training, and related services, are intended 
                for general informational and educational purposes only. We do not provide legal, clinical, or medical 
                advice, and our input should not be relied upon as a substitute for professional judgement in those areas.
              </p>
            </div>
            
            <p className="text-sm sm:text-base leading-relaxed text-gray-700">
              All decisions based on the advice or materials we provide must be independently reviewed and validated by 
              the client. EVIDENT RESEARCH CONSULTANCY LTD accepts no liability for actions taken or not taken based on 
              the use of our research advice or outputs.
            </p>

            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setShowDisclaimerModal(false)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClinicalResearchForm;
